import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/CarScene";

const inputCls = "rounded-2xl bg-white/5 border-white/10 text-white placeholder:text-white/40";
const btnCls = "w-full rounded-full font-bold bg-[#0E1A13] text-[#22C55E] border border-[#1DAF5A]/50 hover:bg-[#12241A] shadow-[0_8px_32px_hsla(145,72%,40%,0.25)] active:scale-95 transition-all duration-150";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
      setStep(2);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: password });
      toast.success("Password reset! Log in with your new password.");
      navigate("/login");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <AuthShell tagline="Locked out? We'll get you back on the road in a minute.">
      <h1 className="font-display font-black text-3xl">Reset password</h1>
      <p className="text-sm text-white/50 mt-1">
        {step === 1 ? "Enter your account email and we'll send a 6-digit reset code." : `Code sent to ${email}. Enter it with your new password.`}
      </p>
      <form onSubmit={step === 1 ? sendCode : reset}
        className="bg-[#121417] border border-white/10 rounded-2xl p-6 mt-6 space-y-4 shadow-[0_16px_48px_-16px_rgba(0,0,0,0.8)]">
        <Input data-testid="forgot-email-input" type="email" required placeholder="Account email"
          value={email} onChange={(e) => setEmail(e.target.value)} disabled={step === 2} className={inputCls} />
        {step === 2 && (
          <>
            <Input data-testid="reset-code-input" inputMode="numeric" maxLength={6} required placeholder="6-digit code"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className={`${inputCls} text-center text-lg tracking-[0.5em] font-bold`} />
            <Input data-testid="reset-password-input" type="password" required minLength={6} placeholder="New password (min 6 chars)"
              value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          </>
        )}
        <Button data-testid={step === 1 ? "forgot-submit-btn" : "reset-submit-btn"} disabled={busy} type="submit" className={btnCls}>
          {busy ? "Working…" : step === 1 ? "Send reset code" : "Reset password"}
        </Button>
        {step === 2 && (
          <button data-testid="resend-reset-btn" type="button" onClick={sendCode}
            className="w-full text-xs font-bold text-white/50 hover:text-white transition-colors duration-200">
            Resend code
          </button>
        )}
      </form>
      <p className="text-sm text-center mt-4 text-white/50">
        Remembered it? <Link to="/login" data-testid="forgot-to-login" className="font-bold text-[#22C55E]">Back to login</Link>
      </p>
    </AuthShell>
  );
}
